/**
 * Which module each screen and each API path belongs to - the feature flags (cmms feature-flags.yml, the registry;
 * owner, 15 Oct 2026). The member module has no flag: a path that matches nothing here is the member module's, and is
 * always on.
 */
export const FLAGGED_MODULES = ["applications", "bookings", "activities", "registers", "meetings", "publicPages",
  "selfRegistration"];

/** Screens - app paths (as in routes/index.js, without /app) and public ones (App.js). First match wins. */
const SCREENS = [
  ["/my-account/applications", "applications"],
  ["/my-account/bookings", "bookings"],
  ["/my-account/certificates", "registers"],
  ["/my-account/action-items", "meetings"],
  ["/service-applications", "applications"],
  ["/apply", "applications"], // the public application form, its verification and its status page
  ["/bookings", "bookings"],
  ["/clergy-availability", "bookings"],
  ["/service-catalog", "bookings"],
  ["/activities", "activities"],
  ["/baptisms", "registers"],
  ["/marriages", "registers"],
  ["/fithat", "registers"],
  ["/certificates/verify", "registers"], // public certificate verification
  ["/meetings", "meetings"],
  // The public page asked by a church's code; the default church's (/, /sv...) is not a module's screen: with the module
  // off it is the sign-in, as it always was (cmms public-page-plan.md, 2.2).
  ["/c", "publicPages"],
  ["/am/c", "publicPages"],
  ["/sv/c", "publicPages"],
  ["/en/c", "publicPages"],
  ["/public-calendar", "publicPages"], // the staff screens (session 7)
  ["/public-profile", "publicPages"],
  ["/class-calendar", "publicPages"],
  // Self-registration: the member module's one public form, with a flag of its own (cmms public-page-plan.md, 7).
  ["/register", "selfRegistration"],
];

/**
 * The backend's paths, after /cmms - the roots of the controllers each flag switches off (cmms
 * ModulesSwitchedOffRealRequestTest derives the same roots from the controllers themselves). The application consent
 * text is the one path of an otherwise-on controller that goes with a module.
 */
const REQUESTS = [
  ["/api/consent-texts/application", "applications"],
  ["/api/service-applications", "applications"],
  ["/api/bookings", "bookings"],
  ["/api/clergy-availability", "bookings"],
  ["/api/service-catalog", "bookings"],
  ["/api/activities", "activities"],
  ["/api/activity-sessions", "activities"],
  ["/api/activity-attendance", "activities"],
  ["/api/activity-consent-texts", "activities"],
  ["/api/activity-non-session-dates", "activities"],
  ["/api/activity-types", "activities"],
  ["/api/funding-tags", "activities"],
  ["/api/baptisms", "registers"],
  ["/api/marriages", "registers"],
  ["/api/fithat-records", "registers"],
  ["/api/certificates", "registers"],
  ["/api/meetings", "meetings"],
  ["/api/action-items", "meetings"],
  ["/api/public/parish", "publicPages"],
  ["/api/public-events", "publicPages"],
  ["/api/public-event-cancellations", "publicPages"],
  ["/api/public-profiles", "publicPages"],
  ["/api/class-calendar", "publicPages"],
  ["/api/class-profiles", "publicPages"],
  ["/api/department-profiles", "publicPages"],
  ["/api/class-days", "publicPages"],
  ["/api/class-day-names", "publicPages"],
  ["/api/user-account/self-register", "selfRegistration"],
];

function under(path, prefix) {
  return path === prefix || path.startsWith(prefix + "/");
}

/** The module a screen belongs to, or null for the member module. Takes "/app/baptisms/register" or "/baptisms". */
export function moduleOfScreen(path) {
  const normalized = (path || "").replace(/^\/app(?=\/|$)/, "");
  const match = SCREENS.find(([prefix]) => under(normalized, prefix));
  return match ? match[1] : null;
}

/** The module a backend request belongs to, or null for the member module. Takes "/cmms/api/baptisms/1" or "/api/...". */
export function moduleOfRequest(path) {
  const normalized = (path || "").replace(/^\/cmms(?=\/)/, "");
  const match = REQUESTS.find(([prefix]) => under(normalized, prefix));
  return match ? match[1] : null;
}
